import {BaseQueryFn} from '@reduxjs/toolkit/query'
import {stringify} from 'qs'

import {ErrorResponse} from '../../booking/types/ErrorResponse'

export type Method =
  | 'get'
  | 'GET'
  | 'delete'
  | 'DELETE'
  | 'head'
  | 'HEAD'
  | 'options'
  | 'OPTIONS'
  | 'post'
  | 'POST'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH'
  | 'purge'
  | 'PURGE'
  | 'link'
  | 'LINK'
  | 'unlink'
  | 'UNLINK'

interface FetchBaseQueryArgs {
  url: string
  method?: Method
  data?: unknown
  params?: Record<string, unknown>
  headers?: Record<string, string>
}

export interface FetchBaseQueryError {
  status?: number
  data: ErrorResponse
  headers: Record<string, string>
}

export type FetchBaseQueryFn = BaseQueryFn<
  FetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError
>

export type FetchBaseQueryFnWithRetry = BaseQueryFn<
  FetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError,
  {maxRetries?: number}
>

const headersToRecord = (headers: Headers): Record<string, string> => {
  const record: Record<string, string> = {}
  headers.forEach((value, key) => {
    record[key] = value
  })
  return record
}

/**
 * Custom base query using native fetch, compatible with RTK Query.
 * Preserves the same error shape and meta.headers format as the former axiosBaseQuery.
 * @param root0 - Configuration object with baseUrl to prepend to all request URLs.
 * @param root0.baseUrl - Base URL to prepend to all request URLs.
 */
export const fetchBaseQuery =
  ({baseUrl}: {baseUrl: string} = {baseUrl: ''}): FetchBaseQueryFn =>
  async ({url, method = 'GET', data, params, headers}) => {
    try {
      const fetchUrl = new URL(baseUrl + url, globalThis.location?.origin)

      if (params)
        fetchUrl.search = stringify(params, {
          arrayFormat: 'brackets',
          encodeValuesOnly: true,
          skipNulls: true
        })

      const hasBody = data !== undefined && data !== null
      const requestHeaders: Record<string, string> = {
        ...(hasBody ? {'Content-Type': 'application/json'} : {}),
        ...headers
      }

      const result = await fetch(fetchUrl, {
        method,
        body: hasBody ? JSON.stringify(data) : undefined,
        headers: requestHeaders
      })

      const responseHeaders = headersToRecord(result.headers)
      const responseData = await result.json().catch(() => undefined)

      if (!result.ok) {
        return {
          error: {
            status: result.status,
            data: responseData ?? result.statusText,
            headers: responseHeaders
          }
        }
      }

      return {data: responseData, meta: {headers: responseHeaders}}
    } catch (error) {
      return {
        error: {
          status: undefined,
          data: `${(error as Error)?.message ?? 'Network error'} [url: ${baseUrl}${url}]` as unknown as ErrorResponse,
          headers: {} as Record<string, string>
        }
      }
    }
  }
